import { useEffect, useState, type ReactNode } from "react";

import { statsCalibration, type StatsCalibration } from "@/lib/stats";

import { useChartHover } from "./chart/HoverCard";
import { clamp } from "./chart/scale";

import { SkeletonRows } from "./Skeleton";
import { Panel, Td, Th } from "./Panel";

const pct = (v: number | null, digits = 1) => (v === null ? "—" : `${(v * 100).toFixed(digits)}%`);

/**
 * A recall rate as a share of the track, mapped over the range the bands cover.
 *
 * `scale.ts`'s `percent` against a 0–100 domain would put every bar between 50% and 100% of the
 * track, where a 6-point gap is 6% of the width and invisible. Mapping 50–100 onto the whole track
 * doubles it, which is the difference between a chart and a decoration.
 */
const rateWidth = (rate: number) => clamp(((rate * 100 - 50) / 50) * 100, 0, 100);

/**
 * The panel's heading, and the one note that is written twice — here and in `Recall.tsx`'s
 * skeleton, which draws this card's ten band rows while `/stats/calibration` is in flight.
 *
 * Exported for that reason. This panel is the only one on the tab that owns its own request, so
 * its loading shape lives in a different file from the panel itself, and a title spelled in both
 * places is a title free to disagree with itself across a repaint.
 */
export const CALIBRATION_PANEL = {
  title: "較正",
  check: "calibration",
  note: "全履歴・遡って採点",
} as const;

/**
 * Calibration — predicted recall at review time against what actually happened.
 *
 * Below the line means FSRS was overconfident, which means intervals are too long. **The panel most
 * likely to explain a mature pass rate of 79% against a 90% target**, and the only thing in Stats
 * that needs a job — `revlog` has no predicted-recall column, so scoring is a backend call per card.
 *
 * Two overlapping bars per band, light for predicted and dark for actual, because the comparison is
 * the content: a band's own pair has to read as one claim rather than as two series.
 */
export function Calibration({ deck, onOpenJob }: { deck: string; onOpenJob?: () => void }) {
  const [data, setData] = useState<StatsCalibration | null>(null);
  const [failed, setFailed] = useState(false);
  const { onEnter, onLeave, card } = useChartHover();

  useEffect(() => {
    let live = true;
    setData(null);
    setFailed(false);
    void statsCalibration(deck)
      .then((found) => live && setData(found))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [deck]);

  if (failed) return null;
  if (!data) {
    // **A cached read, so this is normally a single frame** — the nightly job did the work and this
    // route answers in ~7 ms. It gets a skeleton anyway rather than a spinner, because a spinner
    // for one frame is a flash and the ten band rows are a shape worth holding still.
    return (
      <Panel {...CALIBRATION_PANEL} gap={8}>
        <SkeletonRows rows={10} height={13} gap={16} />
      </Panel>
    );
  }

  // Three empty states, and they are genuinely different problems. Saying "no data" for all three
  // would send the reader to the wrong place twice out of three.
  if (!data.computed) {
    return (
      <Panel {...CALIBRATION_PANEL} note={`採点ジョブが必要 · ${deck}`}>
        {/* **The deck is named**, because the curve is cached per deck and the job scores one at a
            time. A run that scored a different deck completes successfully and leaves this panel
            saying exactly what it says when the job has never run at all — so the empty state has
            to say which deck it is empty *for*. The deck name and the job id are both user- and
            server-owned identifiers and stay exactly as they arrive. */}
        <p className="text-[14px]/[1.55] text-ink-3">
          <span className="font-mono">{deck}</span>{" "}
          は未計算です。予測再生率はどこにも保存されていないため、各カードの復習履歴から再生し直す必要があり、そのためにジョブになっています。ジョブはここで選んでいるデッキを採点します。{" "}
          <button onClick={onOpenJob} className="text-accent-text underline">
            設定で <span className="font-mono">{data.jobId}</span> を実行または予約
          </button>
          。
        </p>
      </Panel>
    );
  }

  if (data.reviewsScored === 0) {
    return (
      <Panel {...CALIBRATION_PANEL} note="採点対象なし">
        <p className="text-[14px]/[1.55] text-ink-3">
          ジョブは実行されましたが、記憶状態のある復習が見つかりませんでした —{" "}
          {data.cardsSkipped.toLocaleString()}枚を除外。FSRS
          が一度もスケジュールしていないコレクションはこう見えます。
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      {...CALIBRATION_PANEL}
      note={`全履歴 · ${data.cardsScored.toLocaleString()}枚を採点、${data.cardsSkipped.toLocaleString()}枚を除外`}
      gap={9}
      right={
        <span className="flex items-center gap-[12px]">
          <Key color="var(--cal-predicted)" label="予測" />
          <Key color="var(--cal-actual)" label="実測" />
        </span>
      }
    >
      <table className="w-full">
        <thead>
          <tr>
            <Th align="left" width={78}>帯</Th>
            <Th />
            <Th width={72}>復習数</Th>
            <Th width={62}>予測</Th>
            <Th width={62}>実測</Th>
            <Th width={58}>差</Th>
          </tr>
        </thead>
        <tbody>
          {data.bands.map((band) => (
            <tr key={band.low} className="border-t border-line-soft">
              <td className="py-[7px] pr-[10px] font-mono text-[14px]/[19.6px] font-semibold text-ink">
                {band.low}–{band.low + 5}
              </td>
              <td className="py-[6px] pr-[10px]">
                {/* **The bars encode the RATE, not the review count.** Drawn to the count they
                    came out within a pixel of each other — the two are nearly equal by
                    construction — and the difference between predicted and actual *is* the
                    panel's entire content. Both are mapped over 50–100%, the range the bands
                    cover, so a 6-point gap is 12% of the track rather than 6% of nothing.

                    The count is not lost: it has its own column, and a band too thin to have a
                    rate draws no dark bar at all. */}
                {/* **The track is the mark, not the two bars inside it.** They overlap by
                    construction — that overlap is the panel's entire content — so hovering either
                    one would answer for a shape the reader cannot point at separately. `overflow`
                    has to go with it: the hover ring is drawn outside the box and a clipping
                    container would cut it off on three sides, so the bars are rounded themselves
                    instead. */}
                <div
                  className="chart-mark relative h-[13px] rounded-[var(--r-2)]"
                  style={{ background: "var(--line-soft)" }}
                  onMouseEnter={(event) =>
                    onEnter(event, {
                      title: `予測${band.low}〜${band.low + 5}%`,
                      rows: [
                        { label: "復習数", value: band.count.toLocaleString() },
                        { label: "予測", value: pct(band.predicted) },
                        { label: "実測", value: pct(band.actual) },
                        {
                          label: "差",
                          value:
                            band.gap === null
                              ? "率を出すには少なすぎます"
                              : `${band.gap > 0 ? "+" : ""}${band.gap.toFixed(1)}pt`,
                        },
                      ],
                    })
                  }
                  onMouseLeave={onLeave}
                >
                  <span
                    className="absolute inset-y-0 left-0 rounded-[var(--r-2)]"
                    style={{
                      width: `${rateWidth(band.predicted)}%`,
                      background: "var(--cal-predicted)",
                    }}
                  />
                  {band.actual !== null && (
                    <span
                      className="absolute inset-y-0 left-0 rounded-[var(--r-2)]"
                      style={{
                        width: `${rateWidth(band.actual)}%`,
                        background: "var(--cal-actual)",
                      }}
                    />
                  )}
                </div>
              </td>
              <Td tone="muted">{band.count.toLocaleString()}</Td>
              <Td tone="muted">{pct(band.predicted)}</Td>
              <Td>{pct(band.actual)}</Td>
              <Td
                color={
                  band.gap === null
                    ? "var(--ink-4)"
                    : band.gap < 0
                      ? "var(--accent-text)"
                      : "var(--ink-3)"
                }
              >
                {band.gap === null ? "—" : `${band.gap > 0 ? "+" : ""}${band.gap.toFixed(1)}`}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* The frames put the summary across the foot as captioned figures rather than in a
          key/value list — five numbers that are read together, not looked up one at a time. */}
      <div className="grid gap-[13px] border-t border-line-soft pt-[12px] sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <Figure
          label="平均差"
          value={
            data.meanGap === null
              ? "—"
              : `${data.meanGap > 0 ? "+" : ""}${data.meanGap.toFixed(1)}pt`
          }
          tone={data.meanGap !== null && data.meanGap < 0 ? "var(--accent-text)" : undefined}
        />
        {/* `N / M`, not `N of M`: the English preposition has no short Japanese equivalent that
            reads as a figure, and this is a mono column where a slash lines up with the rates
            above it. The canvas draws it the same way. */}
        <Figure
          label="予測を下回る帯"
          value={`${data.bandsBelow} / ${data.bandsMeasured}`}
        />
        <Figure
          label={`予測${pct(data.target, 0)}時の実測`}
          value={pct(data.actualAtTarget)}
        />
        <Figure
          label="実質の間隔倍率"
          value={
            data.impliedIntervalMultiplier === null
              ? "—"
              : `${data.impliedIntervalMultiplier.toFixed(2)}×`
          }
        />
        {/* **`Reviews scored`, which is the design's fifth figure** — the denominator every band
            above is drawn from. The cards-scored and cards-skipped pair moved to the panel's note:
            a skipped count is a finding (a run that scored 200 of 11,000 cards is not a success)
            and it is not one of the five figures the design reads together. */}
        <Figure label="採点した復習数" value={data.reviewsScored.toLocaleString()} />
      </div>

      {/* **No explanatory footer.** It printed the seventeen FSRS parameters, then the date the
          job ran; both are gone (Alex, 8 Aug 2026). The parameters are two lines of
          `0.910152, 1.51233, 3.23927, …` that nobody reads, and the run date belongs to the job, not
          to a panel about retention. Both are still stored beside the curve for a session
          diagnosing a suspicious band. */}
      {card}
    </Panel>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex flex-col gap-[3px]">
      <Caption>{label}</Caption>
      <span
        className="font-mono text-[17px]/[1.2] font-semibold"
        style={{ color: tone ?? "var(--ink)" }}
      >
        {value}
      </span>
    </div>
  );
}

function Caption({ children }: { children: ReactNode }) {
  return (
    <span className="text-[14px]/[19.6px] font-semibold uppercase tracking-[.5px] text-ink-3">
      {children}
    </span>
  );
}

function Key({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-[5px] text-[14px]/[1.4] text-ink-2">
      <span aria-hidden className="h-[11px] w-[11px] rounded-[var(--r-2)]" style={{ background: color }} />
      {label}
    </span>
  );
}
